import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import CommonModal from '@/components/common/CommonModal';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IAddCategoryForm, ICategory } from '@/interfaces/Category/category.interface';

interface AddCategoryProps {
    showAddModal: boolean;
    setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
    modalMode: 'create' | 'edit' | 'addSub';
    selectedCategory?: ICategory | null;
    onSuccess?: () => void;
}

export const AddCategory: React.FC<AddCategoryProps> = ({
    showAddModal,
    setShowAddModal,
    modalMode,
    selectedCategory,
    onSuccess,
}) => {
    const isSubCategory = modalMode === 'addSub' || (modalMode === 'edit' && selectedCategory?.parentId);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        control,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<IAddCategoryForm>();

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
        } else {
            setPreviewUrl(null);
        }
    };

    useEffect(() => {
        if (showAddModal) {
            if (modalMode === 'edit' && selectedCategory) {
                console.log(modalMode);
                reset({
                    name: selectedCategory.name,
                    description: selectedCategory.description,
                    status: selectedCategory.status,
                } as IAddCategoryForm);
                setPreviewUrl(selectedCategory.media?.url || null);
            } else {
                console.log(modalMode);
                reset({
                    name: '',
                    description: '',
                    status: 'active',
                } as IAddCategoryForm);
                setPreviewUrl(null);
            }
        }
    }, [showAddModal, modalMode, selectedCategory, reset]);

    const onSubmit = async (data: IAddCategoryForm) => {
        try {
            const formData = new FormData();
            formData.append('name', data.name?.trim());
            if (!isSubCategory) {
                formData.append('description', data.description?.trim());
            }

            if (modalMode === 'addSub' && selectedCategory) {
                formData.append('parentId', selectedCategory.id);
            }

            if (data.file && data.file.length > 0) {
                formData.append('file', data.file[0]);
            }

            const url = modalMode === 'edit' && selectedCategory
                ? `${API_URL + API_ENDPOINT.categories}/${selectedCategory.id}`
                : `${API_URL + API_ENDPOINT.categories}`;

            const method = modalMode === 'edit' ? 'PATCH' : 'POST';

            const response = await callApi<any>(
                url,
                {
                    method: method,
                    multipart: true,
                    data: formData,
                },
            );

            const successMsg = modalMode === 'edit'
                ? (isSubCategory ? 'Sub Category updated successfully' : 'Category updated successfully')
                : modalMode === 'addSub'
                    ? 'Sub Category created successfully'
                    : 'Category created successfully';

            showToastNotification('success', response?.message || successMsg);
            reset();
            setShowAddModal(false);
            onSuccess?.();

        } catch (error) {
            ThrowError(error);
        }
    };

    const handleClose = (open: boolean) => {
        if (!open) {
            reset();
        }
        setShowAddModal(open);
    };



    return (
        <CommonModal
            open={showAddModal}
            size="md"
            onOpenChange={handleClose}
            title={
                modalMode === 'edit'
                    ? (isSubCategory ? "Edit Sub Category" : "Edit Category")
                    : modalMode === 'addSub'
                        ? `Add Sub Category to ${selectedCategory?.name}`
                        : "Create New Category"
            }
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

                <FormField
                    label="Category Name"
                    name="name"
                    type="text"
                    requiredIcon
                    placeholder="e.g. Breakfast"
                    error={errors.name?.message}
                    {...register('name', {
                        ...errorTypes.required('Category Name'),
                    })}
                />

                {!isSubCategory && <FormField
                    label="Description"
                    name="description"
                    type="textarea"
                    rows={2}
                    requiredIcon
                    placeholder="Describe this category"
                    error={errors.description?.message}
                    {...register('description', {
                        ...errorTypes.required('Description'),
                    })}
                />}


                <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                        {isSubCategory ? "Sub-Category Image (Optional)" : "Category Image"} {!isSubCategory && <span className="text-red-500">*</span>}
                    </label>
                    <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.mp4,.pdf,.txt,.rtf"
                        className="w-full border p-2 rounded-md bg-background text-sm text-foreground file:border-0 file:bg-primary file:text-primary-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md cursor-pointer"
                        {...register('file', {
                            required: !isSubCategory && !previewUrl ? 'Category Image is required' : false,
                            onChange: handleImageChange,
                        })}
                    />
                    {previewUrl && (
                        <div className="mt-3 relative w-max">
                            <img
                                src={previewUrl.startsWith('blob:') ? previewUrl : (previewUrl.startsWith('http') ? previewUrl : `${API_URL}/${previewUrl}`)}
                                alt="Category Preview"
                                className="w-24 h-24 object-cover rounded-md border"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=No+Image';
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewUrl(null);
                                    setValue('file', null as any); // Clear the file input in react-hook-form
                                }}
                                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm hover:bg-destructive/90 transition-colors"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                    {errors.file && (
                        <p className="text-red-500 text-sm mt-1">{errors.file.message}</p>
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-border mt-4">
                    <button
                        type="button"
                        onClick={() => handleClose(false)}
                        className="h-9 px-4 rounded-md border text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                    >
                        Cancel
                    </button>
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        loadingText={
                            modalMode === 'edit'
                                ? "Updating..."
                                : modalMode === 'addSub'
                                    ? "Creating Sub..."
                                    : "Creating..."
                        }
                    >
                        {
                            modalMode === 'edit'
                                ? "Update Category"
                                : modalMode === 'addSub'
                                    ? "Create Sub Category"
                                    : "Create Category"
                        }
                    </Button>
                </div>
            </form>
        </CommonModal>
    );
};
